import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { doc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "../firebase";

function toDatetimeLocal(str) {
  if (!str) return "";
  const [datePart, timePart] = str.split(" ");
  if (timePart) {
    const [hh, mm] = timePart.split(":");
    return `${datePart}T${hh}:${mm}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return `${str}T00:00`;
  }
  return str;
}

function fromDatetimeLocal(dt) {
  if (!dt) return "";
  const [datePart, timePart] = dt.split("T");
  const [hh, mm] = timePart.split(":");
  return `${datePart} ${hh}:${mm}:00`;
}

export default function MemberCardManager({ member, onCardSaved }) {
  const [showCardForm, setShowCardForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardData, setCardData] = useState({
    cardNumber: "",
    name: "",
    validFrom: "",
    validTo: "",
    pin: "",
    doors: [false, false, false, false],
  });
  const [existingCardNumber, setExistingCardNumber] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // States for swipe dialog
  const [swipeDialogOpen, setSwipeDialogOpen] = useState(false);
  const [swipeLoading, setSwipeLoading] = useState(false);

  const showSnackbar = useCallback((msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const callEndpoint = useCallback(
    async (fnName, params = {}) => {
      try {
        const fn = httpsCallable(functions, fnName);
        const response = await fn(params);
        return response.data;
      } catch (err) {
        console.error("Error calling endpoint:", err);
        showSnackbar(`Error: ${err.message}`);
        throw err;
      }
    },
    [showSnackbar]
  );

  const handleAssignExistingCard = useCallback(async () => {
    if (!existingCardNumber) {
      showSnackbar("Please enter an existing card number.");
      return;
    }
    if (!member?.id) {
      showSnackbar("No member ID found to update Firestore.");
      return;
    }
    try {
      await updateDoc(doc(db, "members", member.id), {
        cardNumber: parseInt(existingCardNumber, 10),
      });
      showSnackbar(`Assigned existing card number ${existingCardNumber} to member.`);
    } catch (err) {
      showSnackbar(`Error assigning card number: ${err.message}`);
    }
  }, [existingCardNumber, member, showSnackbar]);

  const onViewOrEditCard = useCallback(async () => {
    setShowCardForm(true);
    setLoading(true);

    const firestoreCardNumber = member?.cardNumber;
    if (!firestoreCardNumber) {
      showSnackbar("No Card Number. Ready to add a new card.");
      setCardData({
        cardNumber: "",
        name: "",
        validFrom: "",
        validTo: "",
        pin: "",
        doors: [false, false, false, false],
      });
      setLoading(false);
      return;
    }

    try {
      const getResp = await callEndpoint("getCard", {
        cardNumber: parseInt(firestoreCardNumber, 10),
      });

      if (!getResp?.successfull || !getResp.value) {
        showSnackbar("No record for that card. Fill in form to create.");
        setCardData({
          cardNumber: firestoreCardNumber.toString(),
          name: "",
          validFrom: "",
          validTo: "",
          pin: "",
          doors: [false, false, false, false],
        });
        setLoading(false);
        return;
      }

      const { number, valid, doors, pin } = getResp.value;
      const nameResp = await callEndpoint("readNameByCard", {
        cardNumber: parseInt(firestoreCardNumber, 10),
      });
      const deviceName = nameResp?.value || "";
      const from = toDatetimeLocal(valid?.from);
      const to = toDatetimeLocal(valid?.to);
      const doorArray = [
        !!doors["1"],
        !!doors["2"],
        !!doors["3"],
        !!doors["4"],
      ];

      setCardData({
        cardNumber: number.toString(),
        name: deviceName,
        validFrom: from,
        validTo: to,
        pin: pin && pin !== 0 ? pin.toString() : "",
        doors: doorArray,
      });
      showSnackbar("Loaded card data.");
    } catch {
      // errors shown in callEndpoint
    }
    setLoading(false);
  }, [member, callEndpoint, showSnackbar]);

  const handleSaveCard = useCallback(async () => {
    const fieldsMissing =
      !cardData.cardNumber.trim() ||
      !cardData.name.trim() ||
      !cardData.validFrom.trim() ||
      !cardData.validTo.trim() ||
      !cardData.pin.trim();

    if (fieldsMissing) {
      showSnackbar("All fields (card number, name, validFrom, validTo, and PIN) are required.");
      return;
    }

    try {
      const fromDate = fromDatetimeLocal(cardData.validFrom);
      const toDate = fromDatetimeLocal(cardData.validTo);
      const doorFlagsStr = cardData.doors.map((d) => (d ? "1" : "0")).join(" ");
      const putResp = await callEndpoint("putCard", {
        cardNumber: parseInt(cardData.cardNumber, 10),
        userName: cardData.name.trim(),
        from: fromDate,
        to: toDate,
        doorFlags: doorFlagsStr,
        pin: parseInt(cardData.pin, 10) || 0,
      });

      if (putResp.successfull) {
        showSnackbar("Card saved successfully.");
        if (member?.id) {
          await updateDoc(doc(db, "members", member.id), {
            cardNumber: parseInt(cardData.cardNumber, 10),
            doorAccess: cardData.doors,
          });
          showSnackbar("Member Card Updated.");
          if (typeof onCardSaved === "function") onCardSaved();
        }
        // Exit edit mode
        setShowCardForm(false);
      } else {
        showSnackbar(putResp.info || "Member Card Update Failed");
      }
    } catch {
      // errors shown in callEndpoint
    }
  }, [cardData, member, callEndpoint, showSnackbar]);

  const handleToggleActive = useCallback(() => {
    setCardData((prev) => {
      const isActive = prev.doors.some((d) => d);
      const newDoors = isActive
        ? [false, false, false, false]
        : [true, true, true, true];
      return { ...prev, doors: newDoors };
    });
  }, []);

  const fillNameWithMember = useCallback(() => {
    const combinedName = `${member?.firstName || ""} ${member?.lastName || ""}`.trim();
    setCardData((prev) => ({ ...prev, name: combinedName }));
  }, [member]);

  const handleOpenSwipeDialog = useCallback(() => {
    setSwipeDialogOpen(true);
  }, []);

  const handleCloseSwipeDialog = useCallback(() => {
    setSwipeDialogOpen(false);
  }, []);

  const handleConfirmSwipe = useCallback(async () => {
    setSwipeLoading(true);
    try {
      const resp = await callEndpoint("lastSwipedCard");
      const cardNum = resp?.value ?? resp?.cardNumber;
      if (cardNum !== undefined && cardNum !== null) {
        setCardData((prev) => ({ ...prev, cardNumber: cardNum.toString() }));
        showSnackbar(`Card number retrieved: ${cardNum}`);
        setSwipeDialogOpen(false);
      } else {
        showSnackbar("Failed to retrieve card number.");
      }
    } catch {
      // error handled above
    } finally {
      setSwipeLoading(false);
    }
  }, [callEndpoint, showSnackbar]);

  const isCurrentlyActive = cardData.doors.some((d) => d);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        24/7 Access Details
      </Typography>

      {/* No card assigned yet */}
      {!member?.cardNumber && !showCardForm && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            No Card Number for member.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Existing Card Number"
                fullWidth
                value={existingCardNumber}
                onChange={(e) => setExistingCardNumber(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="contained" onClick={handleAssignExistingCard}>
                Assign Existing
              </Button>
            </Grid>
          </Grid>
          <Button variant="contained" onClick={onViewOrEditCard}>
            Add / Edit Card
          </Button>
        </Paper>
      )}

      {/* View existing card */}
      {!showCardForm && member?.cardNumber && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Card Number: {member.cardNumber}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Access Status: {isCurrentlyActive ? "Active" : "Inactive"}
          </Typography>
          <Button variant="contained" onClick={onViewOrEditCard}>
            View / Edit Card
          </Button>
        </Paper>
      )}

      {/* Card form */}
      {showCardForm && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {/* Card Number + Swipe */}
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  label="Card Number"
                  fullWidth
                  value={cardData.cardNumber}
                  onChange={(e) =>
                    setCardData((prev) => ({ ...prev, cardNumber: e.target.value }))
                  }
                />
                <Button
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleOpenSwipeDialog}
                >
                  Swipe New Card
                </Button>
              </Grid>

              {/* Name */}
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  label="Name"
                  fullWidth
                  value={cardData.name}
                  onChange={(e) =>
                    setCardData((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
                <Button
                  variant="text"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={fillNameWithMember}
                >
                  Use Member Name
                </Button>
              </Grid>

              {/* PIN */}
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  label="PIN"
                  fullWidth
                  value={cardData.pin === "0" ? "" : cardData.pin}
                  onChange={(e) =>
                    setCardData((prev) => ({ ...prev, pin: e.target.value }))
                  }
                />
              </Grid>

              {/* Valid From */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  type="datetime-local"
                  label="Valid From"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={cardData.validFrom}
                  onChange={(e) =>
                    setCardData((prev) => ({ ...prev, validFrom: e.target.value }))
                  }
                />
              </Grid>

              {/* Valid To */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  type="datetime-local"
                  label="Valid To"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={cardData.validTo}
                  onChange={(e) =>
                    setCardData((prev) => ({ ...prev, validTo: e.target.value }))
                  }
                />
              </Grid>

              {/* Access Status & Toggle */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Door Access: {isCurrentlyActive ? "Enabled" : "Disabled"}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleToggleActive}
                >
                  {isCurrentlyActive
                    ? "Disable Access"
                    : "Enable Access"}
                </Button>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12} sm={6}>
                <Button variant="contained" onClick={handleSaveCard}>
                  Save Card
                </Button>
              </Grid>
            </Grid>
          )}

          {/* Swipe Dialog */}
          <Dialog open={swipeDialogOpen} onClose={handleCloseSwipeDialog}>
            <DialogTitle>Swipe New Card</DialogTitle>
            <DialogContent>
              <Typography>
                Swipe the new card you want to assign to this member, then press Confirm.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSwipeDialog} disabled={swipeLoading}>
                Cancel
              </Button>
              <Button onClick={handleConfirmSwipe} disabled={swipeLoading}>
                {swipeLoading ? <CircularProgress size={24} /> : "Confirm"}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="info" sx={{ width: "100%" }} onClose={handleSnackbarClose}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
